// TestWelcomeScreen.tsx
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { AlertCircle, Clock, FileText, User } from 'lucide-react';
import { toast } from 'react-toastify';
import Backdrop from 'components/Backdrop';
import { verifyTestLink } from 'api/apiHelper';

interface TestInfo {
  valid: boolean;
  applicant_name: string;
  job_title: string;
  status: string;
  time_limit_minutes: number;
  total_questions: number;
}

interface TestWelcomeScreenProps {
  testToken?: string;
  onTestStarted?: () => void;
}

const TestWelcomeScreen: React.FC<TestWelcomeScreenProps> = ({ testToken: propTestToken, onTestStarted }) => {
  const { testToken: paramTestToken } = useParams<{ testToken: string }>();
  const testToken = propTestToken || paramTestToken;
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>('');
  const [testInfo, setTestInfo] = useState<TestInfo | null>(null);

  useEffect(() => {
    if (testToken) {
      verifyTest();
    }
  }, [testToken]);

  const verifyTest = async () => {
    setLoading(true);
    setError('');
    
    try {
      const response = await verifyTestLink(testToken!);
      console.log('Test verification:', response.data);
      
      if (response.data.valid) {
        setTestInfo(response.data);
      } else {
        setError(response.data.message || 'Invalid test link');
        toast.error(response.data.message || 'Invalid test link');
      }
    } catch (error: any) {
      console.error('Test verification error:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Failed to verify test link';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleStartTest = async () => {
    // In unified flow, start the test and trigger progression check
    if (window.location.pathname.includes('/unified-test/')) {
      try {
        const { startTest } = await import('api/apiHelper');
        await startTest(testToken!);
        toast.success('Test started! Loading...');
        // Trigger progression check in UnifiedTestFlow
        if (onTestStarted) {
          setTimeout(() => {
            onTestStarted();
          }, 500);
        }
      } catch (error: any) {
        toast.error('Failed to start test. Please try again.');
      }
    } else {
      navigate(`/test/${testToken}`);
    }
  };

  if (loading) {
    return <Backdrop message="Verifying test link..." />;
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-xl p-8 max-w-md w-full">
          <div className="flex items-center justify-center w-16 h-16 bg-red-100 rounded-full mx-auto mb-4">
            <AlertCircle className="w-8 h-8 text-red-600" />
          </div>
          <h2 className="text-2xl font-bold text-center text-gray-800 mb-2">Invalid Test Link</h2>
          <p className="text-center text-gray-600 mb-6">{error}</p>
          <p className="text-sm text-center text-gray-500">
            Please check your email for the correct link or contact support.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl p-8 max-w-2xl w-full">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-indigo-100 rounded-full mb-4">
            <FileText className="w-10 h-10 text-brand-500" />
          </div>
          <h1 className="text-3xl font-bold text-gray-800 mb-2">
            Assessment Test
          </h1>
          <p className="text-gray-600">Welcome to your evaluation</p>
        </div>

        <div className="bg-indigo-50 rounded-lg p-6 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="flex items-center">
              <User className="w-5 h-5 text-brand-500 mr-3" />
              <div>
                <p className="text-sm text-gray-600">Applicant</p>
                <p className="font-semibold text-gray-800">{testInfo?.applicant_name}</p>
              </div>
            </div>
            <div className="flex items-center">
              <FileText className="w-5 h-5 text-brand-500 mr-3" />
              <div>
                <p className="text-sm text-gray-600">Position</p>
                <p className="font-semibold text-gray-800">{testInfo?.job_title}</p>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-gray-50 rounded-lg p-6 mb-6">
          <h3 className="font-semibold text-gray-800 mb-4">Test Instructions:</h3>
          <ul className="space-y-3 text-gray-700">
            <li className="flex items-start">
              <span className="inline-block w-6 h-6 bg-brand-500 text-white rounded-full flex-shrink-0 mr-3 text-center text-sm leading-6 font-semibold">1</span>
              <span>This test contains <strong>{testInfo?.total_questions} multiple choice questions</strong></span>
            </li>
            <li className="flex items-start">
              <span className="inline-block w-6 h-6 bg-brand-500 text-white rounded-full flex-shrink-0 mr-3 text-center text-sm leading-6 font-semibold">2</span>
              <span>You have <strong>{testInfo?.time_limit_minutes} minutes</strong> to complete the test (1 minute per question)</span>
            </li>
            <li className="flex items-start">
              <span className="inline-block w-6 h-6 bg-brand-500 text-white rounded-full flex-shrink-0 mr-3 text-center text-sm leading-6 font-semibold">3</span>
              <span>Once started, the timer cannot be paused</span>
            </li>
            <li className="flex items-start">
              <span className="inline-block w-6 h-6 bg-brand-500 text-white rounded-full flex-shrink-0 mr-3 text-center text-sm leading-6 font-semibold">4</span>
              <span>You can review and change answers before submitting</span>
            </li>
            <li className="flex items-start">
              <span className="inline-block w-6 h-6 bg-brand-500 text-white rounded-full flex-shrink-0 mr-3 text-center text-sm leading-6 font-semibold">5</span>
              <span>The test will auto-submit when time expires</span>
            </li>
          </ul>
        </div>

        <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-6">
          <div className="flex">
            <AlertCircle className="w-5 h-5 text-yellow-600 mr-3 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm text-yellow-800">
                <strong>Important:</strong> Make sure you have a stable internet connection. 
                The test cannot be resumed if the window is closed.
              </p>
            </div>
          </div>
        </div>

        <button
          onClick={handleStartTest}
          className="w-full bg-brand-500 hover:bg-brand-600 text-white font-semibold py-4 px-6 rounded-lg transition duration-200 flex items-center justify-center"
        >
          <Clock className="w-5 h-5 mr-2" />
          Start Test
        </button>

        <p className="text-center text-sm text-gray-500 mt-4">
          By starting the test, you agree to complete it in one sitting.
        </p>
      </div>
    </div>
  );
};

export default TestWelcomeScreen;